import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getFirebaseServices } from '@/lib/firebase/client';
import { onAuthStateChanged } from 'firebase/auth';
import { Mail } from 'lucide-react';

export function AuthGuard({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const [checking, setChecking] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    const services = getFirebaseServices();
    if (!services) {
      // No Firebase config - redirect to login
      navigate(`/auth/login?redirect=${pathname}`);
      return;
    }

    const unsubscribe = onAuthStateChanged(services.auth, (user) => {
      if (user) {
        if (user.emailVerified) {
          setAuthenticated(true);
          setShowVerificationPrompt(false);
        } else {
          // Authenticated but not verified: show verification prompt
          setAuthenticated(false);
          setShowVerificationPrompt(true);
        }
        setChecking(false);
      } else {
        // Not authenticated - redirect to login
        navigate(`/auth/login?redirect=${pathname}`);
      }
    });

    return () => unsubscribe();
  }, [navigate, pathname]);

  if (checking || !authenticated) {
    // If we're checking, show loading spinner. If auth check finished but
    // the user is not authenticated because email is unverified, show the
    // verification prompt UI instead of redirecting.
    if (checking) {
      return (
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-lime-400 border-t-transparent"></div>
            <p className="mt-4 text-sm text-white/60">Loading...</p>
          </div>
        </div>
      );
    }

    if (showVerificationPrompt) {
      const services = getFirebaseServices();

      const checkVerification = async () => {
        setVerifying(true);
        setStatus(null);
        try {
          if (!services) {
            setStatus("Firebase not configured.");
            setVerifying(false);
            return;
          }
          const { auth } = services;
          const user = auth.currentUser;
          if (!user) {
            setStatus("No user found. Please sign in again.");
            setVerifying(false);
            return;
          }
          await user.reload();
          if (user.emailVerified) {
            setStatus("✅ Email verified! Redirecting...");
            setShowVerificationPrompt(false);
            setAuthenticated(true);
            // Navigate to the intended path
            setTimeout(() => navigate(pathname), 800);
          } else {
            setStatus("❌ Not yet verified. Please check your inbox (and spam folder) and click the verification link.");
          }
        } catch (err: any) {
          console.error("Verification check error", err);
          setStatus(err?.message || "Failed to check verification status");
        } finally {
          setVerifying(false);
        }
      };

      return (
        <div className="flex min-h-screen items-center justify-center px-4">
          <div className="space-y-4 rounded-3xl border border-white/10 bg-zinc-900/70 p-6 text-white text-center max-w-md w-full">
            <div className="mx-auto w-16 h-16 rounded-full bg-lime-400/20 flex items-center justify-center">
              <Mail className="h-8 w-8 text-lime-400" />
            </div>
            <h2 className="text-2xl font-semibold">Verify your email</h2>
            <p className="text-sm text-white/60">
              You need to verify your email to access this page. We sent a verification link to your email — check your inbox and spam folder if you don't see it.
            </p>
            <button
              onClick={checkVerification}
              disabled={verifying}
              className="w-full rounded-2xl bg-lime-400 px-4 py-3 text-center font-semibold text-black disabled:opacity-50"
            >
              {verifying ? "Checking..." : "Verification completed"}
            </button>
            {status && (
              <p className={status.startsWith("✅") ? "text-sm text-emerald-300" : status.startsWith("❌") ? "text-sm text-red-300" : "text-sm text-white/70"}>
                {status}
              </p>
            )}
          </div>
        </div>
      );
    }

    // For any other case (not authenticated and not showing verification prompt), show loading.
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-lime-400 border-t-transparent"></div>
          <p className="mt-4 text-sm text-white/60">Loading...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
